import { supabase } from '@/lib/supabase';
import { posthog } from '@/lib/posthog';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Surface enum mirrors `DeepLinkSurface` in `apps/web/src/lib/deep-links.ts`. */
export type DeepLinkClickSurface =
  | 'workbench'
  | 'signal'
  | 'model'
  | 'variables';

export interface DeepLinkClickPayload {
  surface: DeepLinkClickSurface;
  /**
   * URL search-param KEY list ONLY. Never values — agent-emitted deep links
   * can carry user-typed cell refs / namedRanges; logging the value would
   * trip the analytics-PII contract. The API enforces the shape via Zod
   * (`z.array(z.string()).max(10)`) — this is just defense-in-depth.
   */
  params_keys: string[];
}

/**
 * Fire-and-forget click telemetry for `<DeepLink>` chips. Logs the click via
 * `POST /api/usage/deep-link-click`; the API surfaces the event to LangSmith
 * for join with the corresponding conversational-agent run's
 * `deep_links_emitted` count.
 *
 * Failures are swallowed — observability MUST NEVER block the user's
 * navigation. Auth header is best-effort (no session → unauth → API rejects
 * with 401, which we ignore).
 *
 * SHIPPED `agent-ux-optimization` Phase 4 Session 4 (2026-05-07).
 */
export function trackDeepLinkClick(payload: DeepLinkClickPayload): void {
  // NEW Phase 3 § 3.4 — PostHog product event: deep-link chip clicked.
  // Fires synchronously (before the async API POST) so the event is
  // captured even if the navigation fires before the fetch resolves.
  // Only params_keys (never values) per the analytics-PII contract above.
  posthog.capture('deep_link_clicked', {
    surface: payload.surface,
    params_keys: payload.params_keys,
  });

  void (async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      await fetch(`${API_URL}/api/usage/deep-link-click`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(payload),
      });
    } catch {
      /* swallow — analytics-only path */
    }
  })();
}
